// ============================================================
// Me.tsx — 「あなた」ページ (/me)
// ============================================================
//
// self_doc（週次バッチが蒸留した「生きたドキュメント」）を
// セクション順に表示する。各 item はテキスト + 「根拠を見る」展開
// （evidence_entry_ids → diary_entries を id で引いて日付 + 本文抜粋）。
//
// frameworks セクション配下に旧 MBTI/Big5 等の self_analysis 詳細を
// 折り畳みで残す（捨てない）。phase_timeline セクションは Roots 相当の
// 年表表示（life_story_user_stages）。
//
// 「物語」（Story）を隣接タブとして同ページに統合。partner（AIチャット）
// は別物なので独立のまま（このページからは触らない）。
// ============================================================

import { useCallback, useEffect, useMemo, useState } from 'react'
import { supabase } from '@/lib/supabase'
import { Card, EmptyState, PageHeader } from '@/components/ui'
import { fetchPhases, type LifePhase } from '@/lib/phases'
import { SelfAnalysis } from '@/pages/SelfAnalysis'
import { recordButtonClick } from '@/lib/usageTelemetry'

// ---------------------------------------------------------------------------
// Types — self_doc row / sections
// ---------------------------------------------------------------------------

type SectionKind =
  | 'current_patterns'
  | 'phase_changes'
  | 'blind_spots'
  | 'frameworks'
  | 'phase_timeline'

interface DocItem {
  text: string
  evidence_entry_ids?: number[]
  evidence_note?: string
}

interface DocSection {
  kind: SectionKind
  title: string
  phase_label?: string
  items: DocItem[]
}

interface SelfDocRow {
  id: string
  version: number
  is_current: boolean
  current_phase_key: string | null
  headline: string | null
  sections: DocSection[] | null
  model_used: string | null
  generated_from: Record<string, unknown> | null
  created_at: string
}

interface DiaryEntryLite {
  id: number
  entry_date: string
  body: string
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** 先頭・末尾の全角/半角カッコを剥がす（self_doc の phase_label が「（…）」付きで来ることがある）。 */
function stripWrappingParens(s: string): string {
  return s.trim().replace(/^[（(]+/, '').replace(/[）)]+$/, '').trim()
}

/** frameworks item の text が "big5: ..." のように analysis_type プレフィックスで始まることがある → 消す。 */
function stripTypePrefix(s: string): string {
  return s.replace(/^[a-z_]+:\s*/, '').trim()
}

/** created_at から「◯日前」相当の文字列を作る。 */
function relativeDays(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime()
  const days = Math.floor(ms / 86400000)
  if (days <= 0) return '今日'
  if (days === 1) return '昨日'
  if (days < 7) return `${days}日前`
  if (days < 30) return `${Math.floor(days / 7)}週間前`
  if (days < 365) return `${Math.floor(days / 30)}ヶ月前`
  return `${Math.floor(days / 365)}年以上前`
}

/** life_story_user_stages のフェーズを「ラベル（YYYY/MM〜YYYY/MM）」表記にする。 */
function formatPhaseRange(p: LifePhase): string {
  const fmt = (d: string | null): string => {
    if (!d) return ''
    const [y, m] = d.split('-')
    return `${y}/${m}`
  }
  const start = fmt(p.dateStart)
  const end = p.dateEnd ? fmt(p.dateEnd) : '現在'
  if (!start) return p.label
  return `${p.label}（${start}〜${end}）`
}

// ---------------------------------------------------------------------------
// Evidence drill-down
// ---------------------------------------------------------------------------

function EvidenceToggle({ entryIds, note }: { entryIds: number[]; note?: string }) {
  const [open, setOpen] = useState(false)
  const [entries, setEntries] = useState<DiaryEntryLite[] | null>(null)
  const [loading, setLoading] = useState(false)

  const load = useCallback(async () => {
    if (entries) return
    setLoading(true)
    const { data } = await supabase
      .from('diary_entries')
      .select('id, entry_date, body')
      .in('id', entryIds)
      .order('entry_date', { ascending: false })
    setEntries((data ?? []) as DiaryEntryLite[])
    setLoading(false)
  }, [entries, entryIds])

  if (entryIds.length === 0) {
    // 根拠エントリが無い → note だけ薄く出す（無ければ何も出さない）
    if (!note) return null
    return (
      <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4, fontStyle: 'italic' }}>
        {note}
      </div>
    )
  }

  return (
    <div style={{ marginTop: 6 }}>
      <button
        onClick={() => { recordButtonClick('me:evidence-toggle'); setOpen((v) => !v); if (!open) load() }}
        style={{
          background: 'none', border: 'none', cursor: 'pointer', padding: 0,
          fontSize: 11, color: 'var(--accent2, var(--accent))', fontFamily: 'var(--font)',
        }}
      >
        {open ? '根拠を閉じる' : '根拠を見る'}
        {note ? <span style={{ color: 'var(--text3)' }}> · {note}</span> : null}
      </button>
      {open && (
        <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {loading && <div style={{ fontSize: 11, color: 'var(--text3)' }}>読み込み中…</div>}
          {entries?.map((e) => (
            <div key={e.id} style={{
              padding: '8px 12px', background: 'var(--surface2)', borderRadius: 6,
              fontSize: 12, color: 'var(--text3)', lineHeight: 1.6,
            }}>
              <span style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text3)', marginRight: 8 }}>
                {e.entry_date}
              </span>
              <span style={{ fontStyle: 'italic' }}>
                {(e.body ?? '').slice(0, 150)}{(e.body ?? '').length > 150 ? '…' : ''}
              </span>
            </div>
          ))}
          {entries && entries.length === 0 && !loading && (
            <div style={{ fontSize: 11, color: 'var(--text3)' }}>該当する日記が見つかりませんでした</div>
          )}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Section renderers
// ---------------------------------------------------------------------------

/** 通常のテキストリスト型セクション（current_patterns / phase_changes / blind_spots / frameworks）。 */
function DocItemList({ items, isFrameworks }: { items: DocItem[]; isFrameworks?: boolean }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {items.map((it, i) => {
        const text = isFrameworks ? stripTypePrefix(it.text) : it.text
        const ids = it.evidence_entry_ids ?? []
        return (
          <div key={i}>
            <div style={{ fontSize: 14, color: 'var(--text)', lineHeight: 1.8 }}>{text}</div>
            <EvidenceToggle entryIds={ids} note={it.evidence_note} />
          </div>
        )
      })}
    </div>
  )
}

/** phase_timeline セクション。self_doc の items があればそれを使い、無ければ fetchPhases から年表を組む。 */
function PhaseTimeline({ items, phases }: { items: DocItem[]; phases: LifePhase[] }) {
  // 優先: self_doc が用意した年表 items（数字なし表記）。fallback: phases から組む。
  const lines: string[] = items.length > 0
    ? items.map((it) => stripTypePrefix(it.text))
    : phases.map(formatPhaseRange)
  if (lines.length === 0) {
    return <div style={{ fontSize: 12, color: 'var(--text3)' }}>まだフェーズの記録がありません。</div>
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      {lines.map((line, i) => (
        <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '6px 0' }}>
          <div style={{
            width: 8, height: 8, borderRadius: '50%', flexShrink: 0, marginTop: 7,
            background: i === lines.length - 1 ? 'var(--accent)' : 'var(--text3)',
          }} />
          <div style={{ fontSize: 13, color: 'var(--text)', lineHeight: 1.7 }}>{line}</div>
        </div>
      ))}
    </div>
  )
}

/** frameworks セクション全体（折り畳み内 + さらにその中に「もっと詳しく」で旧 self_analysis 全文）。 */
function FrameworksSection({ items }: { items: DocItem[] }) {
  const [showDetail, setShowDetail] = useState(false)
  return (
    <div>
      <DocItemList items={items} isFrameworks />
      <details style={{ marginTop: 16 }}>
        <summary style={{
          cursor: 'pointer', fontSize: 12, color: 'var(--text2)', userSelect: 'none',
        }}>
          フレームワーク（MBTI / Big5 / StrengthsFinder …）の詳細を見る
        </summary>
        <div style={{ marginTop: 12 }}>
          {!showDetail ? (
            <button
              onClick={() => { recordButtonClick('me:frameworks-detail'); setShowDetail(true) }}
              style={{
                background: 'none', border: '1px solid var(--border)', borderRadius: 6,
                padding: '6px 12px', fontSize: 12, color: 'var(--text2)', cursor: 'pointer',
                fontFamily: 'var(--font)',
              }}
            >
              もっと詳しく（MBTI・Big5 などの全文を読み込む）
            </button>
          ) : (
            <div style={{ marginTop: 4 }}>
              <SelfAnalysis />
            </div>
          )}
        </div>
      </details>
    </div>
  )
}

// ---------------------------------------------------------------------------
// 「あなた」タブ本体
// ---------------------------------------------------------------------------

export function MirrorSection() {
  const [doc, setDoc] = useState<SelfDocRow | null>(null)
  const [phases, setPhases] = useState<LifePhase[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setLoading(true)
      const [docRes, phasesRes] = await Promise.all([
        supabase
          .from('self_doc')
          .select('id, version, is_current, current_phase_key, headline, sections, model_used, generated_from, created_at')
          .eq('is_current', true)
          .order('version', { ascending: false })
          .limit(1),
        fetchPhases(),
      ])
      if (cancelled) return
      const row = (docRes.data?.[0] as SelfDocRow | undefined) ?? null
      setDoc(row)
      setPhases(phasesRes)
      setLoading(false)
    })()
    return () => { cancelled = true }
  }, [])

  const sections = useMemo<DocSection[]>(() => {
    if (!doc?.sections) return []
    return doc.sections.filter((s) => s && Array.isArray(s.items))
  }, [doc])

  if (loading) {
    return <div style={{ color: 'var(--text3)', fontSize: 13 }}>Loading...</div>
  }

  if (!doc || sections.length === 0) {
    return (
      <Card>
        <EmptyState
          icon="🌱"
          message="まだ分析がありません。日記が溜まると自動で更新されます。"
        />
      </Card>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {doc.headline && (
        <Card glow>
          <div style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6 }}>
            今わかっていること
          </div>
          <div style={{ fontSize: 16, color: 'var(--text)', lineHeight: 1.8, fontWeight: 500 }}>
            {doc.headline}
          </div>
        </Card>
      )}

      {sections.map((sec, si) => {
        const phaseLabel = sec.phase_label ? stripWrappingParens(sec.phase_label) : null
        const isBlindSpots = sec.kind === 'blind_spots'
        return (
          <Card
            key={si}
            glow={isBlindSpots}
            style={isBlindSpots ? { borderLeft: '3px solid var(--accent)' } : undefined}
          >
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 15, color: 'var(--text)', fontWeight: 600 }}>
                {isBlindSpots ? '🔎 ' : ''}{sec.title}
              </div>
              {sec.kind === 'current_patterns' && phaseLabel && (
                <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 2 }}>{phaseLabel}</div>
              )}
              {isBlindSpots && (
                <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 2, lineHeight: 1.6 }}>
                  自分では気づきにくいところ。ここに、一番大事なヒントがあることが多い。
                </div>
              )}
            </div>
            {sec.kind === 'frameworks' ? (
              <FrameworksSection items={sec.items} />
            ) : sec.kind === 'phase_timeline' ? (
              <PhaseTimeline items={sec.items} phases={phases} />
            ) : (
              <DocItemList items={sec.items} />
            )}
          </Card>
        )
      })}

      <div style={{ fontSize: 11, color: 'var(--text3)', textAlign: 'center', paddingTop: 4 }}>
        最終更新: version {doc.version} · {relativeDays(doc.created_at)}
      </div>
    </div>
  )
}

export function Me() {
  return (
    <div className="page">
      <PageHeader
        title={<>あなた<strong>について</strong></>}
        description="日記から見えてきたパターン・変化・盲点。各項目は日記の根拠にひもづいています。"
      />
      <MirrorSection />
    </div>
  )
}
